import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import Cookies from "js-cookie";
import ConsoleSwitcher from "../components/ConsoleSwitcher";
import { HugeiconsIcon } from "@hugeicons/react";
import PackageAddIcon from "@hugeicons/core-free-icons/PackageAddIcon";
import UserAdd01Icon from "@hugeicons/core-free-icons/UserAdd01Icon";
import AddTeamIcon from "@hugeicons/core-free-icons/AddTeamIcon";
import Location01Icon from "@hugeicons/core-free-icons/Location01Icon";
import FileUploadIcon from "@hugeicons/core-free-icons/FileUploadIcon";
import { useModalMain } from "../../contexts/modal";
import { CreateOrder } from "../../components/modal-forms/create-order";
import { CreateUser } from "../../components/modal-forms/create-user";
import CreateUserWithPatient from "../../components/modal-forms/create-user/CreateUserWithPatient";
import GetServiceability from "../../components/modal-forms/get-servicability/GetServicability";
import UploadFilesLSQ from "../../components/modal-forms/upload-files-lsq/UploadFilesLSQ";
import { getUserRoles } from "../../utils/helpers";
import { ROLES } from "../../utils/constants";

// The prototype's five topbar buttons already exist as legacy modal forms
// (src/layouts/Dashboard/components/topbar/index.jsx) opened through the
// same ModalProvider this app already mounts above the router in
// src/index.js — so `/v2` reuses them directly rather than re-implementing
// or deep-linking away. Role lists are copied from that file's
// TOPBAR_BUTTONS_ROLE_MAP; gating is per-button, matching legacy, not one
// gate for the whole bar.
//
// Shared by both consoles rather than duplicated per console: every entry is already
// role-gated individually, and each list here happens to include PHARMACIST, so a
// pharmacist sees exactly the subset that applies to them without a second config.
// A console that needs a different set passes `actions`.
export const DEFAULT_TOPBAR_ACTIONS = [
    {
        key: "new-order",
        label: "New Order",
        icon: PackageAddIcon,
        variant: "primary",
        Component: CreateOrder,
        roles: [ROLES.ADMIN, ROLES.PHARMACIST, ROLES.SUPER_ADMIN],
    },
    {
        key: "new-user",
        label: "New User",
        icon: UserAdd01Icon,
        variant: "primary",
        Component: CreateUser,
        roles: [ROLES.ADMIN, ROLES.PHARMACIST, ROLES.SUPER_ADMIN, ROLES.SUPPORT, ROLES.SUPER_SUPPORT],
    },
    {
        key: "new-user-patient",
        label: "New User + Patient",
        icon: AddTeamIcon,
        variant: "primary",
        Component: CreateUserWithPatient,
        roles: [ROLES.ADMIN, ROLES.PHARMACIST, ROLES.SUPER_ADMIN, ROLES.SUPPORT, ROLES.SUPER_SUPPORT],
    },
    {
        key: "check-serviceability",
        label: "Check Serviceability",
        icon: Location01Icon,
        variant: "secondary",
        Component: GetServiceability,
        roles: [ROLES.ADMIN, ROLES.SUPER_ADMIN, ROLES.SUPER_SUPPORT, ROLES.SUPPORT],
    },
    {
        key: "upload-lsq",
        label: "Upload LSQ Files",
        icon: FileUploadIcon,
        variant: "secondary",
        Component: UploadFilesLSQ,
        roles: [ROLES.ADMIN, ROLES.SUPER_ADMIN, ROLES.SUPER_SUPPORT, ROLES.SUPPORT],
    },
];

// Legacy's own topbar buttons, verbatim from
// src/layouts/Dashboard/components/topbar/index.jsx. Legacy has no hover lift and
// no shadow on these, so both are gone.
const PRIMARY_CLASS =
    "bg-elm-500 text-white-50 px-4 py-3 rounded-2xl font-sans font-semibold flex justify-center items-center gap-2";

// Legacy's secondary is OUTLINED, not a pale fill — the mint-filled variant was a
// v2 invention and read as a different green beside the solid buttons.
const SECONDARY_CLASS =
    "border-2 border-elm-500 text-elm-500 hover:bg-elm-500 hover:text-white-50 px-4 py-3 rounded-2xl font-sans font-semibold flex justify-center items-center gap-2 transition-colors";

function AppTopbar({ actions }) {
    const { openModal } = useModalMain();
    const [userName, setUserName] = useState("");
    const userRoles = getUserRoles();

    useEffect(() => {
        const raw = Cookies.get("user");
        if (raw) {
            try {
                setUserName(JSON.parse(raw).name || "");
            } catch {
                setUserName("");
            }
        }
    }, []);

    const visibleButtons = actions.filter((button) => button.roles.some((role) => userRoles.includes(role)));
    const initial = userName ? userName.trim().charAt(0).toUpperCase() : "?";

    return (
        <header className="flex flex-wrap items-center justify-between gap-4 border-b border-[var(--ops-carbon-200)] bg-[var(--ops-carbon-25)] px-6 py-4 text-[var(--ops-carbon-900)] shadow-[var(--ops-shadow-topbar)]">
            <div className="flex flex-wrap items-center gap-2.5">
                {visibleButtons.map((button) => (
                    <button
                        key={button.key}
                        type="button"
                        onClick={() => openModal(button.Component, button.label, {}, false)}
                        className={button.variant === "primary" ? PRIMARY_CLASS : SECONDARY_CLASS}
                    >
                        <HugeiconsIcon icon={button.icon} size={16} strokeWidth={1.6} />
                        {button.label}
                    </button>
                ))}
            </div>

            <div className="flex items-center gap-2.5 font-sans font-bold">
                {/* Mounted here rather than per-console: AppTopbar is shared by both
                    v2 AppShell instances, so one mount covers ops and pharmacy. It
                    replaces the single "Legacy Console" button that used to sit here
                    and navigate to a hardcoded /home -- which 404s for a pharmacist,
                    since /home is registered for ADMIN/SUPER_ADMIN only. The switcher
                    resolves each console's landing path per role instead. */}
                <ConsoleSwitcher />
                <span className="text-black">
                    {userName ? `Welcome, ${userName}` : "Welcome"}
                </span>
                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-elm-500 font-bold text-white-50">
                    {initial}
                </span>
            </div>
        </header>
    );
}

AppTopbar.propTypes = {
    actions: PropTypes.arrayOf(
        PropTypes.shape({
            key: PropTypes.string.isRequired,
            label: PropTypes.string.isRequired,
            roles: PropTypes.arrayOf(PropTypes.string).isRequired,
        })
    ),
};

AppTopbar.defaultProps = {
    actions: DEFAULT_TOPBAR_ACTIONS,
};

export default AppTopbar;
